import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import './EstimateInfoTooltip.scss';

const TOOLTIP_MARGIN = 8;
const TOOLTIP_GAP = 8;

// Cadence of the while-open occlusion recheck: covering UI that opens from
// React state fires no scroll or resize event, so events alone cannot close
// an already-open tooltip that a modal now paints under.
const OCCLUSION_POLL_MS = 300;

interface TooltipPos {
  top: number;
  left: number;
  // Nudges the arrow back toward the trigger after the body is viewport-clamped.
  arrowOffset: number;
  placement: 'above' | 'below';
}

export interface EstimateInfoTooltipTriggerProps<T extends HTMLElement = HTMLElement> {
  ref: React.Ref<T>;
  onClick: (event: React.MouseEvent<T>) => void;
  onKeyDown: (event: React.KeyboardEvent<T>) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  'aria-label': string;
  'aria-expanded': boolean;
  'aria-describedby'?: string;
}

interface EstimateInfoTooltipProps<T extends HTMLElement = HTMLElement> {
  ariaLabel: string;
  children: (triggerProps: EstimateInfoTooltipTriggerProps<T>) => React.ReactElement;
  content: React.ReactNode;
}

const EstimateInfoTooltip = <T extends HTMLElement = HTMLElement>({
  ariaLabel,
  children,
  content,
}: EstimateInfoTooltipProps<T>) => {
  const [isClickOpen, setIsClickOpen] = useState(false);
  const [isHoverOpen, setIsHoverOpen] = useState(false);
  const isOpen = isClickOpen || isHoverOpen;
  const [pos, setPos] = useState<TooltipPos | null>(null);
  const triggerRef = useRef<T | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const tooltipIdRef = useRef(`estimate-info-tooltip-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (tooltipRef.current?.contains(target)) return;
      setIsClickOpen(false);
      setIsHoverOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsClickOpen(false);
        setIsHoverOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPos(null);
      return undefined;
    }

    /**
     * The nearest vertically scrollable ancestor of the trigger — the list
     * panel hosting the card. The tooltip is confined to its box so it never
     * spills over the panel onto the header or the map. Horizontal-only
     * scrollers (card carousels) are skipped: their short box is not the
     * surface the tooltip lives in. Null falls back to the viewport.
     */
    const findBoundsHost = (): HTMLElement | null => {
      for (
        let node = triggerRef.current?.parentElement ?? null;
        node;
        node = node.parentElement
      ) {
        // overflowY alone is not enough: at mobile widths the results list is
        // overflow-y auto but as tall as its content (the sheet above it does
        // the scrolling), so only an ancestor that actually CLIPS counts.
        if (
          /(auto|scroll)/.test(getComputedStyle(node).overflowY)
          && node.scrollHeight > node.clientHeight + 1
        ) {
          return node;
        }
      }
      return null;
    };
    const boundsHost = findBoundsHost();

    const update = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;

      // The confining box: the host panel clipped to the viewport, or the
      // viewport alone when the card sits in the page itself.
      const hostRect = boundsHost?.getBoundingClientRect();
      let bounds = {
        left: Math.max(hostRect?.left ?? 0, 0),
        right: Math.min(hostRect?.right ?? window.innerWidth, window.innerWidth),
        top: Math.max(hostRect?.top ?? 0, 0),
        bottom: Math.min(hostRect?.bottom ?? window.innerHeight, window.innerHeight),
      };
      // A host scrolled fully out of the viewport leaves a degenerate box;
      // fall back to the viewport rather than placing against nothing.
      if (bounds.bottom <= bounds.top || bounds.right <= bounds.left) {
        bounds = { left: 0, right: window.innerWidth, top: 0, bottom: window.innerHeight };
      }

      const rect = trigger.getBoundingClientRect();
      // The tooltip closes only on a user action (outside click, Escape, the
      // trigger toggle) — never on scroll or occlusion. When the anchor card
      // scrolls away it stays open, clamped to the panel edge below. The one
      // non-user close left is cleanup for an unmounted anchor.
      if (!trigger.isConnected) {
        setIsClickOpen(false);
        setIsHoverOpen(false);
        return;
      }

      const desiredCenter = rect.left + rect.width / 2;
      const tipWidth = tooltipRef.current?.offsetWidth ?? 0;
      const tipHeight = tooltipRef.current?.offsetHeight ?? 0;
      const halfWidth = tipWidth / 2;
      const minCenter = bounds.left + halfWidth + TOOLTIP_MARGIN;
      const maxCenter = bounds.right - halfWidth - TOOLTIP_MARGIN;
      const clampedCenter = tipWidth > 0
        ? Math.max(minCenter, Math.min(maxCenter, desiredCenter))
        : desiredCenter;
      const roomBelow = bounds.bottom - rect.bottom;
      const roomAbove = rect.top - bounds.top;
      // 'above' only when below lacks room AND above genuinely fits — never
      // trade a clipped bottom for a tooltip poking off the top of the screen.
      const needed = tipHeight + TOOLTIP_GAP + TOOLTIP_MARGIN;
      const placement: 'above' | 'below' = tipHeight > 0
        && roomBelow < needed
        && roomAbove >= needed
        ? 'above'
        : 'below';

      // Clamp the tooltip box inside the bounds for its placement ('above'
      // hangs the box up from `top`, 'below' hangs it down), so a clipped-away
      // anchor leaves the tooltip pinned at the panel edge instead of riding
      // out of view with the card.
      const desiredTop = placement === 'above' ? rect.top : rect.bottom;
      const minTop = bounds.top + TOOLTIP_MARGIN
        + (placement === 'above' ? tipHeight + TOOLTIP_GAP : 0);
      const maxTop = bounds.bottom - TOOLTIP_MARGIN
        - (placement === 'below' ? tipHeight + TOOLTIP_GAP : 0);
      const clampedTop = tipHeight > 0
        ? Math.max(minTop, Math.min(maxTop, desiredTop))
        : desiredTop;

      setPos({
        top: clampedTop,
        left: clampedCenter,
        arrowOffset: desiredCenter - clampedCenter,
        placement,
      });
    };

    update();
    const raf = requestAnimationFrame(update);
    // Bounded recheck: overlays appearing without scroll/resize (JAA-356 review).
    const poll = window.setInterval(update, OCCLUSION_POLL_MS);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      cancelAnimationFrame(raf);
      window.clearInterval(poll);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen]);

  return (
    <>
      {children({
        ref: triggerRef,
        onClick: (event) => {
          event.stopPropagation();
          if (isClickOpen) {
            setIsClickOpen(false);
            setIsHoverOpen(false);
          } else {
            setIsClickOpen(true);
          }
        },
        onKeyDown: (event) => {
          event.stopPropagation();
        },
        onMouseEnter: () => {
          if (window.matchMedia('(hover: hover)').matches) {
            setIsHoverOpen(true);
          }
        },
        onMouseLeave: () => setIsHoverOpen(false),
        'aria-label': ariaLabel,
        'aria-expanded': isOpen,
        'aria-describedby': isOpen ? tooltipIdRef.current : undefined,
      })}
      {isOpen && createPortal(
        <div
          ref={tooltipRef}
          id={tooltipIdRef.current}
          className="estimate-info-tooltip"
          role="tooltip"
          data-placement={pos?.placement ?? 'above'}
          style={{
            top: pos?.top ?? 0,
            left: pos?.left ?? 0,
            visibility: pos ? 'visible' : 'hidden',
            ['--arrow-offset' as never]: `${pos?.arrowOffset ?? 0}px`,
          }}
        >
          {content}
        </div>,
        document.body,
      )}
    </>
  );
};

export default EstimateInfoTooltip;
